import React from "react";
import { Link } from "react-router-dom";
import ProductImage from "./ProductImage";
import LabVerifiedBadge from "./LabVerifiedBadge";
import { TrendingUp, Flame } from "lucide-react";
import useBadgeMap from "../hooks/useBadgeMap";

const CARD_BADGES = {
  best_seller: { label: "Best Seller", Icon: TrendingUp },
  popular_week: { label: "Popular", Icon: Flame },
};

function CardBadges({ slug }) {
  const map = useBadgeMap();
  const list = (map[slug] || []).filter((k) => CARD_BADGES[k]);
  if (!list.length) return null;
  return (
    <div data-testid={`card-badges-${slug}`} className="contents">
      {list.map((k) => {
        const { label, Icon } = CARD_BADGES[k];
        return (
          <span key={k} data-testid={`card-badge-${k}-${slug}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#00C3FF]/50 text-[#00C3FF] font-mono-tech text-[8px] sm:text-[9px] uppercase tracking-[0.12em] sm:tracking-[0.18em]">
            <Icon size={10} /> {label}
          </span>
        );
      })}
    </div>
  );
}

export default function ProductCard({ product, wholesale = false }) {
  const variants = wholesale
    ? product.variants.filter((v) => v.wholesale_price && v.wholesale_price > 0)
    : product.variants;
  if (!variants.length) return null;
  const fromPrice = Math.min(...variants.map((v) =>
    wholesale ? Number(v.wholesale_price) * 10 : Number(v.price)
  ));
  const sizes = variants.map((v) => v.size).join(" · ");

  return (
    <Link
      to={wholesale ? `/wholesale-shop/${product.slug}` : `/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="product-card group flex flex-col"
    >
      <div className="vial-bg aspect-square flex items-center justify-center overflow-hidden relative">
        <ProductImage
          product={product}
          className="w-[72%] h-[88%] transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div data-testid={`card-badge-strip-${product.slug}`} className="min-h-[80px] sm:min-h-[64px] px-5 py-2.5 flex flex-col justify-center items-start gap-1.5 border-t border-white/5 bg-[#07070D]">
        <LabVerifiedBadge size="inline" slug={product.slug} />
        {!wholesale && <CardBadges slug={product.slug} />}
      </div>
      <div className="p-5 flex-1 flex flex-col gap-2 border-t border-white/5">
        <div className="overline" style={{ fontSize: 10 }}>
          {wholesale ? "WHOLESALE · " : ""}{product.category}
        </div>
        <div className="font-heading text-lg leading-tight">{product.name}</div>
        <div className="font-mono-tech text-[10px] text-[#5C657A] tracking-wider uppercase">{sizes}</div>
        <div className="flex-1" />
        <div className="flex items-baseline justify-between pt-2">
          <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#5C657A] uppercase">
            {wholesale ? "From / kit" : "From"}
          </div>
          <div className="font-mono-tech text-base text-[#00C3FF]">${fromPrice.toFixed(2)}</div>
        </div>
      </div>
    </Link>
  );
}
